/**
 * CQRS data providers.
 *
 * The kit inverts read and write per the locked architecture: reads come from the
 * kernel's `/api/v1` projection, writes go to coreBOS (system of record) via the
 * evo-mcp-corebos gateway. Refine binds a provider *per resource*, not per operation,
 * so a single "reads here / writes there" resource needs a composite provider that
 * dispatches by operation — that is `cqrsProvider`, registered as `default`.
 *
 * The pure providers stay registered too: `kernel` for read-only resources, `corebos`
 * for write-only/system-of-record resources.
 */

import type { DataProvider } from "@refinedev/core";
import { createMCPDataProvider, createKernelDataProvider } from "@evo-flywheel/ui";
import { coreBOSMap } from "@kit/corebos-map";
import { COREBOS_API_URL, KERNEL_API_URL, KERNEL_API_KEY } from "./config";

export const coreBOSProvider = createMCPDataProvider(COREBOS_API_URL);
export const kernelProvider = createKernelDataProvider(
  KERNEL_API_URL,
  KERNEL_API_KEY
);

// The agent spine lives in its descriptor tables, not the kernel entity store; the pack serves
// them under /tables with the same query contract (src/concepts/spine-tables.ts).
const SPINE = new Set(["activity", "deliverable", "journey_definition", "journey_execution"]);
const spineProvider = createKernelDataProvider(`${KERNEL_API_URL}/tables`, KERNEL_API_KEY);
const readerFor = (resource: string) => (SPINE.has(resource) ? spineProvider : kernelProvider);

// Reads key on the descriptor slug (kernel entity `type`); writes must target the
// coreBOS module name. The mcp provider maps `resource` -> module internally via a
// generic vtiger table it does not know flywheel slugs, so translate here and hand
// it the coreBOS module as the resource.
const toCoreBOSModule = (resource: string): string =>
  coreBOSMap[resource]?.coreBOSModule ?? resource;

const asWrite = <T extends { resource: string }>(params: T): T => ({
  ...params,
  resource: toCoreBOSModule(params.resource),
});

const cqrsProvider: DataProvider = {
  // Reads -> kernel projection.
  getList: (params) => readerFor(params.resource).getList(params),
  getOne: (params) => readerFor(params.resource).getOne!(params),
  getMany: (params) => readerFor(params.resource).getMany!(params),
  // Writes -> coreBOS system of record (resource remapped to the coreBOS module).
  create: (params) => coreBOSProvider.create(asWrite(params)),
  update: (params) => coreBOSProvider.update(asWrite(params)),
  deleteOne: (params) => coreBOSProvider.deleteOne!(params),
  custom: (params) => kernelProvider.custom!(params),
  getApiUrl: () => KERNEL_API_URL,
};

export const dataProvider = {
  default: cqrsProvider,
  kernel: kernelProvider,
  corebos: coreBOSProvider,
};
